"use client";

import { useEffect, useRef } from "react";
import * as d3 from "d3";
import type { ActorGraph, ActorLink, ActorNode } from "@/types/geodata";

type SimActorNode = ActorNode & d3.SimulationNodeDatum;
type SimActorLink = ActorLink & d3.SimulationLinkDatum<SimActorNode>;

export function ActorGraphPreview({ graph }: { graph?: ActorGraph }) {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!graph || !ref.current) return;

    const width = 360;
    const height = 220;
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const nodes: SimActorNode[] = graph.nodes.map((node) => ({ ...node }));
    const links: SimActorLink[] = graph.links.map((link) => ({ ...link }));

    const simulation = d3
      .forceSimulation<SimActorNode>(nodes)
      .force("link", d3.forceLink<SimActorNode, SimActorLink>(links).id((node) => node.id).distance(82))
      .force("charge", d3.forceManyBody().strength(-180))
      .force("center", d3.forceCenter(width / 2, height / 2));

    const link = svg
      .append("g")
      .selectAll("line")
      .data(links)
      .join("line")
      .attr("stroke", "rgba(255,255,255,.25)")
      .attr("stroke-width", (d) => 1 + d.strength * 2);

    const node = svg
      .append("g")
      .selectAll("circle")
      .data(nodes)
      .join("circle")
      .attr("r", (d) => 7 + d.pressure / 16)
      .attr("fill", (d) => (d.kind === "population" || d.kind === "movement" ? "#34d399" : "#f87171"));

    const label = svg
      .append("g")
      .selectAll("text")
      .data(nodes)
      .join("text")
      .text((d) => d.label)
      .attr("font-size", 10)
      .attr("fill", "rgba(255,255,255,.78)");

    simulation.on("tick", () => {
      link
        .attr("x1", (d) => (d.source as SimActorNode).x ?? 0)
        .attr("y1", (d) => (d.source as SimActorNode).y ?? 0)
        .attr("x2", (d) => (d.target as SimActorNode).x ?? 0)
        .attr("y2", (d) => (d.target as SimActorNode).y ?? 0);
      node.attr("cx", (d) => d.x ?? 0).attr("cy", (d) => d.y ?? 0);
      label.attr("x", (d) => (d.x ?? 0) + 10).attr("y", (d) => (d.y ?? 0) + 4);
    });

    return () => {
      simulation.stop();
    };
  }, [graph]);

  return (
    <div className="rounded-md border bg-card p-3">
      <p className="mb-2 text-xs uppercase text-muted-foreground">Actor pressure graph</p>
      <svg ref={ref} viewBox="0 0 360 220" className="h-56 w-full" role="img" />
    </div>
  );
}
